/**
 * AreaBriefing — Full-screen interstitial shown before entering each area.
 *
 * Summarizes what the surveyor will inspect: area overview, domain breakdown
 * with item counts, photo requirements, estimated time, and walk order.
 */

import { Camera, Clock, MapPin, ChevronRight } from 'lucide-react'
import type { AreaWalkthrough } from '../../lib/surveyGuide'

interface AreaBriefingProps {
  area: AreaWalkthrough
  /** Number of areas already completed */
  areasCompleted: number
  /** Total areas in walkthrough */
  totalAreas: number
  onBegin: () => void
  onSkip: () => void
}

export function AreaBriefing({ area, areasCompleted, totalAreas, onBegin, onSkip }: AreaBriefingProps) {
  const totalItems = area.sections.reduce((sum, s) => sum + s.items.length, 0)
  const totalPhotos = area.requiredPhotos.reduce((sum, r) => sum + r.minCount, 0)

  return (
    <div className="space-y-4 px-4 py-6">
      {/* Progress */}
      <div className="flex items-center justify-between text-xs text-slate-400">
        <span>Area {areasCompleted + 1} of {totalAreas}</span>
        {area.estimatedMinutes && (
          <span className="flex items-center gap-1">
            <Clock size={12} />
            ~{area.estimatedMinutes} min
          </span>
        )}
      </div>

      {/* Area title */}
      <div className="app-surface p-5 space-y-3 border-l-4 border-[var(--brand-primary)]">
        <div className="flex items-center gap-2">
          <MapPin size={20} className="text-[var(--brand-primary)]" />
          <h2 className="text-lg font-bold text-slate-800">{area.areaLabel}</h2>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed">{area.introduction}</p>
      </div>

      {/* What you'll inspect */}
      <div className="app-surface p-4 space-y-3">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">What You'll Inspect</span>
        <div className="space-y-2">
          {area.sections.map((section) => (
            <div key={section.domain} className="flex items-center justify-between py-1">
              <div className="flex items-center gap-2">
                <span className="brand-chip text-[10px] font-bold">{section.domain}</span>
                <span className="text-sm text-slate-700">{section.domainLabel}</span>
              </div>
              <span className="text-xs text-slate-400">{section.items.length} items</span>
            </div>
          ))}
        </div>
        <div className="pt-1 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <span>{totalItems} inspection items total</span>
          <span className="flex items-center gap-1">
            <Camera size={12} />
            {totalPhotos} required photo(s)
          </span>
        </div>
      </div>

      {/* Photo requirements summary */}
      {area.requiredPhotos.length > 0 && (
        <div className="app-surface p-4 space-y-2 bg-amber-50 border border-amber-200">
          <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
            Required Photos Before Inspection
          </span>
          <ul className="space-y-1">
            {area.requiredPhotos.map((req, idx) => (
              <li key={idx} className="text-xs text-amber-800 flex items-start gap-1.5">
                <Camera size={11} className="mt-0.5 flex-shrink-0" />
                <span>{req.label} ({req.minCount > 1 ? `${req.minCount}+ photos` : '1 photo'})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Walk order */}
      <div className="app-surface p-4 space-y-2">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Suggested Walk Order</span>
        <p className="text-sm text-slate-600 leading-relaxed">{area.walkOrder}</p>
      </div>

      {/* Repeatable room notice */}
      {area.isRepeatable && area.repeatableRooms && (
        <div className="app-surface p-4 space-y-2 bg-blue-50 border border-blue-200">
          <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Multiple Rooms</span>
          <p className="text-xs text-blue-800">
            This area has multiple rooms ({area.repeatableRooms.join(', ')}). You'll select which room
            to inspect and can repeat the checklist for each room.
          </p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={onBegin}
          className="flex-1 py-3 rounded-lg text-sm font-bold bg-[var(--brand-primary)] text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
        >
          Begin This Section
          <ChevronRight size={16} />
        </button>
        <button
          type="button"
          onClick={onSkip}
          className="py-3 px-4 rounded-lg text-sm font-bold bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors"
        >
          Skip
        </button>
      </div>
    </div>
  )
}
